import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Loader2, ArrowLeft, ChevronRight, Eye, Send, CheckCircle2, XCircle, PackageCheck, Archive, RefreshCw, ClipboardCheck, AlertTriangle } from "lucide-react";
import {
  v2AdminListCourses, v2AdminGetCourse, v2AdminCreateCourse,
  v2AdminCreateModule, v2AdminCreateLesson, v2AdminUpdateLesson,
  v2AdminTransition, v2AdminCreateAsset, v2AdminPreflight,
} from "../../lib/api";

const LESSON_TYPES = ["VIDEO", "AUDIO", "TEXT", "PDF_WORKBOOK", "EXERCISE", "START_TASK", "LIVE_SESSION", "SUBMISSION", "ASSESSMENT"];
const RELEASE_STRATS = ["IMMEDIATE", "DAYS_AFTER_ENROLLMENT", "CALENDAR_DATE", "COHORT_DAY", "MANUAL"];
const COMPLETION_RULES = ["MANUAL_CONFIRM", "SELF_ATTEST_READY", "VIDEO_90", "RESOURCE_AND_CONFIRM", "REVIEW_APPROVED"];
const STATUS_COLORS = {
  DRAFT: "text-white/50 border-white/15",
  REVIEW: "text-amber-200 border-amber-200/30",
  REVIEW_APPROVED: "text-cyan-200 border-cyan-200/30",
  SCHEDULED: "text-purple-200 border-purple-200/30",
  PUBLISHED: "text-[#D4AF37] border-[#D4AF37]/40",
  ARCHIVED: "text-white/30 border-white/10",
};

function StatusPill({ s }) {
  return <span data-testid={`status-pill-${s}`} className={`inline-block px-2 py-0.5 rounded-full text-[10px] tracking-wider uppercase border ${STATUS_COLORS[s] || ""}`}>{s}</span>;
}

// -------- List courses --------
export function V2CoursesList() {
  const [courses, setCourses] = useState(null);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ slug: "", product_code: "", title: "", subtitle: "", internal_only: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => { v2AdminListCourses().then(setCourses).catch((e) => setErr(e?.response?.data?.detail || "Fehler")); }, []);
  const create = async () => {
    setBusy(true);
    try { const c = await v2AdminCreateCourse(form); setCourses((cs) => [c, ...(cs || [])]); setShowNew(false); }
    catch (e) { setErr(e?.response?.data?.detail || "Anlegen fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  return (
    <div className="pt-24 container-sat" data-testid="v2-courses-list">
      <Link to="/admin" className="btn-ghost text-sm"><ArrowLeft size={14} /> Admin</Link>
      <div className="flex items-center justify-between mt-6">
        <div>
          <div className="overline">Kurs-Engine V2</div>
          <h1 className="serif text-3xl font-light">Kurse</h1>
        </div>
        <button onClick={() => setShowNew((s) => !s)} className="btn-primary text-sm" data-testid="btn-new-course">
          <Plus size={14} /> Neuer Kurs
        </button>
      </div>
      {showNew && (
        <div className="glass rounded-2xl p-6 mt-4" data-testid="new-course-form">
          <div className="grid md:grid-cols-2 gap-3">
            {["slug", "product_code", "title", "subtitle"].map((k) => (
              <input key={k} placeholder={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            ))}
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm text-white/70">
            <input type="checkbox" checked={form.internal_only} onChange={(e) => setForm({ ...form, internal_only: e.target.checked })} />
            Internal only (INTERNAL_TEST)
          </label>
          <button onClick={create} disabled={busy} className="btn-primary text-sm mt-3">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Anlegen"}
          </button>
        </div>
      )}
      {err && <div className="text-red-300 text-sm mt-4">{err}</div>}
      <div className="mt-6 space-y-2">
        {!courses ? <div className="text-white/45 text-sm">Lade…</div> : courses.length === 0 ? (
          <div className="text-white/45 text-sm">Noch keine V2-Kurse. Lege einen an oder verwende den Seed <code>internal-block3-test</code>.</div>
        ) : courses.map((c) => (
          <Link key={c.id} to={`/admin/v2/courses/${c.id}`}
            data-testid={`course-row-${c.slug}`}
            className="glass rounded-xl p-4 flex items-center gap-3 hover:bg-white/[0.05] transition">
            <div className="flex-1">
              <div className="serif text-lg">{c.title}</div>
              <div className="text-xs text-white/45 mt-0.5">{c.slug} · {c.product_code} · {c.internal_only ? "INTERNAL" : "PUBLIC"}</div>
            </div>
            <StatusPill s={c.status} />
            <ChevronRight size={16} className="text-white/40" />
          </Link>
        ))}
      </div>
    </div>
  );
}

// -------- Course detail / builder --------
export function V2CourseBuilder() {
  const { courseId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const reload = () => { v2AdminGetCourse(courseId).then(setData).catch((e) => setErr(e?.response?.data?.detail || "Fehler")); };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [courseId]);

  const addModule = async () => {
    const title = window.prompt("Modul-Titel");
    if (!title) return;
    await v2AdminCreateModule(courseId, { title, position: (data.modules?.length || 0) + 1 });
    reload();
  };
  const addLesson = async (moduleId) => {
    const title = window.prompt("Lektions-Titel");
    if (!title) return;
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    await v2AdminCreateLesson(courseId, {
      module_id: moduleId, title, slug,
      short_description: "kurz",
      lesson_type: "TEXT", position: (data.lessons?.length || 0) + 1,
      body: "Neue Lektion.", integration_prompt: "?", transfer_48h: "—",
    });
    reload();
  };
  const transition = async (lid, target, extra = {}) => {
    setBusy(true);
    try { await v2AdminTransition(lid, target, extra); reload(); }
    catch (e) { setErr(e?.response?.data?.detail || "Transition fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="pt-32 container-sat text-white/60">{err || "Lade…"}</div>;

  return (
    <div className="pt-24 container-sat" data-testid="v2-course-builder">
      <Link to="/admin/v2/courses" className="btn-ghost text-sm"><ArrowLeft size={14} /> Kurse</Link>
      <div className="flex flex-wrap items-start justify-between mt-6 gap-3">
        <div>
          <div className="overline">V2 Course Builder</div>
          <h1 className="serif text-3xl font-light">{data.title}</h1>
          <div className="text-xs text-white/45 mt-1">{data.slug} · {data.product_code} · Engine v{data.engine_version}</div>
        </div>
        <Link to={`/admin/v2/preview-as-learner/${data.slug}`} className="btn-secondary text-sm" data-testid="btn-preview-as-learner">
          <Eye size={14} /> Vorschau als Teilnehmer
        </Link>
      </div>

      <div className="mt-6">
        <button onClick={addModule} className="btn-secondary text-sm" data-testid="btn-add-module"><Plus size={14} /> Modul</button>
      </div>

      {err && <div className="text-red-300 text-sm mt-4">{err}</div>}

      <div className="mt-6 space-y-4">
        {(data.modules || []).map((m) => {
          const lessons = (data.lessons || []).filter((l) => l.module_id === m.id).sort((a, b) => a.position - b.position);
          return (
            <div key={m.id} className="glass rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="overline">Modul</div>
                  <div className="serif text-xl">{m.title}</div>
                </div>
                <button onClick={() => addLesson(m.id)} className="btn-ghost text-sm" data-testid={`btn-add-lesson-${m.id}`}>
                  <Plus size={14} /> Lektion
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {lessons.map((l) => (
                  <div key={l.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4"
                    data-testid={`lesson-row-${l.slug}`}>
                    <div className="flex items-center gap-3">
                      <div className="text-white/40 text-xs w-6">{l.position}.</div>
                      <div className="flex-1 min-w-0">
                        <div className="serif text-lg truncate">{l.title}</div>
                        <div className="text-[11px] text-white/45 mt-0.5 flex flex-wrap gap-2 items-center">
                          <span>{l.lesson_type}</span> · <span>{l.release_strategy}</span>
                          {l.release_value && <span>· val {l.release_value}</span>}
                          <span>· {l.completion_rule}</span>
                        </div>
                      </div>
                      <StatusPill s={l.status} />
                      <button onClick={() => setEditing(l)} className="btn-ghost text-xs" data-testid={`btn-edit-${l.slug}`}>Bearbeiten</button>
                      <PreflightButton lessonId={l.id} slug={l.slug} />
                    </div>
                    {editing?.id === l.id && (
                      <LessonEditor lesson={l} onSaved={() => { setEditing(null); reload(); }} onCancel={() => setEditing(null)} />
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {l.status === "DRAFT" && (
                        <button onClick={() => transition(l.id, "REVIEW")} disabled={busy} className="btn-ghost text-xs" data-testid={`btn-transition-review-${l.slug}`}>
                          <Send size={12} /> Review anfordern
                        </button>
                      )}
                      {l.status === "REVIEW" && (
                        <>
                          <button onClick={() => transition(l.id, "REVIEW_APPROVED")} disabled={busy} className="btn-secondary text-xs" data-testid={`btn-transition-approved-${l.slug}`}>
                            <CheckCircle2 size={12} /> Freigeben
                          </button>
                          <button onClick={() => transition(l.id, "DRAFT")} disabled={busy} className="btn-ghost text-xs">
                            <XCircle size={12} /> Zurück an Editor
                          </button>
                        </>
                      )}
                      {l.status === "REVIEW_APPROVED" && (
                        <button onClick={() => transition(l.id, "PUBLISHED")} disabled={busy} className="btn-primary text-xs" data-testid={`btn-transition-publish-${l.slug}`}>
                          <PackageCheck size={12} /> Veröffentlichen
                        </button>
                      )}
                      {l.status === "PUBLISHED" && (
                        <button onClick={() => transition(l.id, "ARCHIVED")} disabled={busy} className="btn-ghost text-xs">
                          <Archive size={12} /> Archivieren
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {lessons.length === 0 && <div className="text-white/40 text-xs">Noch keine Lektionen in diesem Modul.</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PreflightButton({ lessonId, slug }) {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setReport(await v2AdminPreflight(lessonId)); setOpen(true); }
    catch (e) { setReport({ error: e?.response?.data?.detail || "Preflight fehlgeschlagen" }); setOpen(true); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={run} disabled={busy} className="btn-ghost text-xs" data-testid={`btn-preflight-${slug}`}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <><ClipboardCheck size={12} /> Preflight</>}
      </button>
      {open && report && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid={`preflight-modal-${slug}`}>
          <div className="glass max-w-lg w-full rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="overline">Publish Preflight</div>
                <div className="serif text-xl mt-1">{slug}</div>
              </div>
              <div className={`px-3 py-1 rounded-full text-[11px] border ${report.publish_ready ? "border-[#D4AF37]/40 text-[#D4AF37]" : "border-red-300/30 text-red-300"}`}
                data-testid={`preflight-ready-${slug}`}>
                {report.publish_ready ? "PUBLISH-READY" : `${report.summary?.blockers || 0} Blocker`}
              </div>
            </div>
            {report.error ? (
              <div className="text-red-300 text-sm mt-4">{report.error}</div>
            ) : (
              <>
                {report.blocking?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <div className="overline text-red-300/80">Blockierend</div>
                    {report.blocking.map((b, i) => (
                      <div key={i} className="rounded-lg border border-red-400/20 bg-red-500/[0.04] p-3" data-testid={`preflight-block-${b.code}`}>
                        <div className="text-red-200 text-xs font-mono">{b.code}</div>
                        <div className="text-white/80 text-sm mt-1">{b.message}</div>
                        <div className="text-white/45 text-[11px] mt-0.5">Feld: {b.field}</div>
                      </div>
                    ))}
                  </div>
                )}
                {report.warnings?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <div className="overline text-amber-300/80">Warnungen</div>
                    {report.warnings.map((b, i) => (
                      <div key={i} className="rounded-lg border border-amber-400/20 bg-amber-500/[0.03] p-3" data-testid={`preflight-warn-${b.code}`}>
                        <div className="text-amber-200 text-xs font-mono flex items-center gap-1"><AlertTriangle size={10} /> {b.code}</div>
                        <div className="text-white/70 text-sm mt-1">{b.message}</div>
                      </div>
                    ))}
                  </div>
                )}
                {report.publish_ready && (
                  <div className="mt-4 text-white/70 text-sm">
                    Alle Pflichtpunkte sind erfüllt. Der Server erlaubt eine Veröffentlichung.
                    <div className="text-white/40 text-xs mt-1">Der Preflight-Report löst niemals selbst eine Veröffentlichung aus.</div>
                  </div>
                )}
              </>
            )}
            <button onClick={() => setOpen(false)} className="btn-ghost text-sm mt-6">Schliessen</button>
          </div>
        </div>
      )}
    </>
  );
}


function LessonEditor({ lesson, onSaved, onCancel }) {
  const [f, setF] = useState({ ...lesson });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [asset, setAsset] = useState({ asset_type: "VIDEO", provider: "INTERNAL_SYNTHETIC", safe_display_name: "" });
  const [assetBusy, setAssetBusy] = useState(false);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const patch = {};
      ["title", "short_description", "body", "integration_prompt", "transfer_48h",
       "safety_note", "support_note", "estimated_minutes",
       "release_strategy", "release_value", "completion_rule",
       "video_asset_id", "audio_asset_id", "workbook_asset_id", "transcript_asset_id"].forEach((k) => {
        if (f[k] !== lesson[k]) patch[k] = f[k];
      });
      if (patch.estimated_minutes) patch.estimated_minutes = parseInt(patch.estimated_minutes) || 10;
      await v2AdminUpdateLesson(lesson.id, patch);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const registerAsset = async () => {
    setAssetBusy(true);
    try {
      const a = await v2AdminCreateAsset({ ...asset, owner_id: lesson.course_id });
      const field = a.asset_type === "VIDEO" ? "video_asset_id"
                  : a.asset_type === "AUDIO" ? "audio_asset_id"
                  : a.asset_type === "PDF" ? "workbook_asset_id" : "transcript_asset_id";
      setF({ ...f, [field]: a.id });
    } catch (e) { setErr(e?.response?.data?.detail || "Asset-Anlage fehlgeschlagen"); }
    finally { setAssetBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-white/10 pt-4" data-testid={`lesson-editor-${lesson.slug}`}>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block col-span-2">
          <div className="overline">Titel</div>
          <input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="block col-span-2">
          <div className="overline">Kurzbeschreibung</div>
          <input value={f.short_description || ""} onChange={(e) => setF({ ...f, short_description: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <div className="overline">Release-Strategie</div>
          <select value={f.release_strategy} onChange={(e) => setF({ ...f, release_strategy: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="edit-release-strategy">
            {RELEASE_STRATS.map((s) => <option key={s} value={s} className="bg-[#060810]">{s}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="overline">Release-Wert (Tage / Datum)</div>
          <input value={f.release_value || ""} onChange={(e) => setF({ ...f, release_value: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="7 · 2026-06-01" />
        </label>
        <label className="block">
          <div className="overline">Completion-Regel</div>
          <select value={f.completion_rule} onChange={(e) => setF({ ...f, completion_rule: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
            {COMPLETION_RULES.map((s) => <option key={s} value={s} className="bg-[#060810]">{s}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="overline">Dauer (min)</div>
          <input type="number" value={f.estimated_minutes || 10} onChange={(e) => setF({ ...f, estimated_minutes: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="block col-span-2">
          <div className="overline">Body</div>
          <textarea value={f.body || ""} onChange={(e) => setF({ ...f, body: e.target.value })} rows={5}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="block col-span-2">
          <div className="overline">Integrationsfrage</div>
          <input value={f.integration_prompt || ""} onChange={(e) => setF({ ...f, integration_prompt: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="block col-span-2">
          <div className="overline">48-Stunden-Transfer</div>
          <input value={f.transfer_48h || ""} onChange={(e) => setF({ ...f, transfer_48h: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="block col-span-2">
          <div className="overline">Sicherheitsnotiz</div>
          <input value={f.safety_note || ""} onChange={(e) => setF({ ...f, safety_note: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </label>
      </div>

      <div className="mt-4 rounded-xl border border-white/10 p-3" data-testid="asset-slot-editor">
        <div className="overline mb-2">Asset registrieren (provider-neutral)</div>
        <div className="grid md:grid-cols-3 gap-2">
          <select value={asset.asset_type} onChange={(e) => setAsset({ ...asset, asset_type: e.target.value })}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
            <option className="bg-[#060810]">VIDEO</option>
            <option className="bg-[#060810]">AUDIO</option>
            <option className="bg-[#060810]">PDF</option>
            <option className="bg-[#060810]">TRANSCRIPT</option>
          </select>
          <select value={asset.provider} onChange={(e) => setAsset({ ...asset, provider: e.target.value })}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
            <option className="bg-[#060810]">PENDING</option>
            <option className="bg-[#060810]">INTERNAL_SYNTHETIC</option>
          </select>
          <input placeholder="Interner Name" value={asset.safe_display_name}
            onChange={(e) => setAsset({ ...asset, safe_display_name: e.target.value })}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </div>
        <button onClick={registerAsset} disabled={assetBusy || !asset.safe_display_name}
          className="btn-ghost text-xs mt-2" data-testid="btn-register-asset">
          {assetBusy ? <Loader2 size={12} className="animate-spin" /> : "Asset anlegen & zuweisen"}
        </button>
      </div>

      <div className="mt-4 text-xs text-white/60 flex flex-wrap gap-4">
        <span>video: {f.video_asset_id || "—"}</span>
        <span>audio: {f.audio_asset_id || "—"}</span>
        <span>workbook: {f.workbook_asset_id || "—"}</span>
      </div>

      {err && <div className="text-red-300 text-xs mt-2">{err}</div>}
      <div className="mt-4 flex gap-2">
        <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="btn-save-lesson">
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Speichern"}
        </button>
        <button onClick={onCancel} className="btn-ghost text-sm">Abbrechen</button>
      </div>
    </div>
  );
}
